import { FieldValidator } from "final-form";
import React, { HTMLInputTypeAttribute, useState } from "react";
import { FieldInputProps } from "react-final-form";
import Icon from "@wff/components/ui/icon";
import { IStringValidator } from "@wff/interfaces/validators";
import { IconTooltip, TooltipIcons } from "../iconTooltip";
import classes from "./TextField.module.scss";

interface IValidateField {
  isValid: boolean;
  validate: FieldValidator<string> | IStringValidator | boolean | undefined;
  value: string;
}

interface Props extends FieldInputProps<any, HTMLElement> {
  label?: string;
  type?: HTMLInputTypeAttribute;
  style?: React.CSSProperties;
  tooltipIcon?: TooltipIcons;
  tooltipData?: string;
  color?: string;
  className?: string;
  inputProps?: Object;
  validate?: FieldValidator<string> | IStringValidator | boolean | undefined;
  formTextFieldValidation?: boolean;
  supressErrorStylesOnFocus?: boolean;
  touched?: boolean;
  withPreview?: boolean;
  showTooltipIconOnErrorOnly?: boolean;
}

// FormTextField validation logic returns undefined when valid and returns an error string when invalid
const validateFormTextField = ({
  isValid,
  validate,
  value,
}: IValidateField): boolean => {
  isValid =
    validate && typeof validate === "function"
      ? validate(value) === undefined || validate(value) === null
      : validate === null || validate === undefined
      ? true
      : validate; // else just use whatever truthy/falsey value is passed to directly represent valid/invalid
  return isValid;
};

// The standard TextField validation logic returns true when valid and returns false when invalid
const validateTextField = ({
  isValid,
  validate,
  value,
}: IValidateField): boolean => {
  isValid =
    validate && typeof validate === "function"
      ? (validate as IStringValidator)(value)
      : validate === null || validate === undefined || validate; // else just use whatever truthy/falsey value is passed to directly represent valid/invalid
  return isValid;
};

export const TextField = React.forwardRef<HTMLInputElement, Props>(
  // eslint-disable-next-line sonarjs/cognitive-complexity
  (props, ref) => {
    const {
      label,
      type = "text",
      style,
      tooltipData,
      tooltipIcon,
      color,
      className,
      validate,
      supressErrorStylesOnFocus,
      formTextFieldValidation,
      touched,
      showTooltipIconOnErrorOnly,
      inputProps,
      withPreview,
      ...rest
    } = props;

    const [inputType, setInputType] = useState(type);

    let value = rest?.value;

    if (!value && ref != null && typeof ref !== "function") {
      value = ref?.current?.value;
    }
    let isValid = true;

    // if TextField is wrapped by FormTextField (or another wrapper component with similar validation needs)
    if (formTextFieldValidation && touched) {
      isValid = validateFormTextField({ isValid, validate, value }); // see declaration for definition notes
    } else if (!formTextFieldValidation && touched) {
      isValid = validateTextField({ isValid, validate, value }); // see declaration for definition notes
    }
    const classNames = [
      classes["input-container"],
      className,
      !isValid && classes.error,
      color &&
        (isValid || supressErrorStylesOnFocus) &&
        classes[`input-${color}`],
    ]
      .join(" ")
      .trim();

    const handleIconClick = () => {
      if (withPreview) {
        setInputType(inputType === "password" ? "text" : "password");
      }
    };
    return (
      <div className={classNames} style={style}>
        <label className={value && classes.filled} htmlFor={rest.id}>
          {label}
        </label>
        <input {...rest} type={inputType} ref={ref} {...inputProps} />
        {withPreview && (
          <Icon
            iconName={inputType === "password" ? "eye-cross" : "eye"}
            className={classes.previewicon}
            aria-label="Password Preview"
            onClick={handleIconClick}
          />
        )}

        <div className={classes.tooltip}>
          {tooltipData &&
            (!showTooltipIconOnErrorOnly ||
              (showTooltipIconOnErrorOnly && !isValid)) && (
              <IconTooltip data={tooltipData} icon={tooltipIcon} />
            )}
        </div>
      </div>
    );
  }
);

TextField.displayName = "TextField";

TextField.defaultProps = {
  label: "",
  type: "text",
  style: undefined,
  tooltipIcon: undefined,
  tooltipData: "",
  color: "blue",
  supressErrorStylesOnFocus: false,
  formTextFieldValidation: false,
  touched: true,
  withPreview: false,
  inputProps: {},
};
